import JSZip from 'jszip';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { INDEX_HTML, TEMPLATE_VERSIONS_CACHE, cacheTemplateZip } from '~/lib/template-zip';

const CONFIG_JSON = 'config.json';

/**
 * Previews a template zip that hasn't been uploaded yet.
 *
 * The service worker checks its cache before fetching, so we can unzip the file
 * here, write the entries under a synthetic template-version id, and let the
 * existing preview path serve it. Nothing is uploaded and no records exist.
 */

/** Only the parts of `config.json` the preview needs. Validation is the server's job. */
const CONFIG_SCHEMA = z
  .object({
    aspectRatio: z.string().optional(),
    params: z
      .array(
        z
          .object({
            array: z.boolean().optional(),
            defaultValue: z.unknown().optional(),
            kind: z.string(),
            name: z.string(),
          })
          .passthrough(),
      )
      .optional(),
  })
  .passthrough();

type TemplateConfig = z.infer<typeof CONFIG_SCHEMA>;

const readConfig = async (zip: JSZip): Promise<TemplateConfig> =>
  CONFIG_SCHEMA.parse(JSON.parse(await zip.files[CONFIG_JSON].async('string')));

/**
 * Shape the neo-client preview harness validates. `kind` is the uppercase
 * `TemplateParamKind` the GraphQL API returns, so config kinds are upcased.
 */
interface PreviewContentItem {
  readonly contentOptions: readonly {
    readonly array: boolean;
    readonly fileId: null;
    readonly id: number;
    readonly kind: string;
    readonly name: string;
    readonly value: unknown;
  }[];
  readonly fileId: number;
  readonly id: number;
  readonly name: string;
  readonly templateUri: string;
}

export interface TemplateZipPreview {
  /** CSS aspect ratio from `config.json`, if it declared a usable one. */
  readonly aspectRatio: string | undefined;
  /** True while unzipping and populating the cache. */
  readonly loading: boolean;
  /**
   * Null when there's nothing renderable. Why a zip isn't renderable is reported
   * by `templateZipValidation`, not here — this hook only decides whether there
   * is something to show.
   */
  readonly payload: PreviewContentItem | null;
  /** True when the environment can't preview — no service worker controlling the page. */
  readonly unavailable: boolean;
}

const EMPTY: TemplateZipPreview = {
  aspectRatio: undefined,
  loading: false,
  payload: null,
  unavailable: false,
};

/**
 * Matches the `aspectRatio` pattern in `public/config.schema.json`. An
 * unvalidated value would reach CSS as `calc(320px * calc(wide))`, which is
 * dropped as invalid and collapses the preview to zero height.
 */
const RATIO_REGEX = /^\d+\/\d+$/;

const usableRatio = (ratio: string | undefined) =>
  ratio != null && RATIO_REGEX.test(ratio) ? ratio : undefined;

/**
 * A synthetic template-version id. Must be numeric to match the service
 * worker's path regex; 13-digit timestamps won't collide with real sequential
 * ids, and a fresh one per zip avoids reusing a previous preview's entries.
 */
const syntheticVersionId = () => Date.now();

// Without a service worker controlling the page nothing will serve the cached
// entries, so there's no point unzipping.
const canPreview = () => navigator.serviceWorker.controller != null;

export const useTemplateZipPreview = (file: File | undefined, name: string) => {
  const [preview, setPreview] = useState<TemplateZipPreview>(EMPTY);

  // Cache keys written for the current preview, so they can be removed.
  const keysRef = useRef<string[]>([]);

  const evict = async () => {
    const keys = keysRef.current;
    keysRef.current = [];
    if (keys.length === 0) return;

    const cache = await caches.open(TEMPLATE_VERSIONS_CACHE);
    await Promise.all(keys.map((key) => cache.delete(key)));
  };

  // `name` is deliberately not a dependency — re-unzipping on every keystroke
  // would be wasteful, and the harness only uses it as a label.
  const nameRef = useRef(name);
  nameRef.current = name;

  useEffect(() => {
    if (file == null) {
      void evict();
      setPreview(EMPTY);
      return;
    }

    if (!canPreview()) {
      setPreview({ ...EMPTY, unavailable: true });
      return;
    }

    let cancelled = false;

    const build = async () => {
      setPreview({ ...EMPTY, loading: true });
      await evict();

      const zip = await JSZip.loadAsync(await file.arrayBuffer());
      // JSZip types `files` as a total record, so membership needs `in`.
      if (!(INDEX_HTML in zip.files)) return setPreview(EMPTY);

      const versionId = syntheticVersionId();
      const baseKey = `${window.location.origin}/template-versions/${versionId}/index.html`;

      const cache = await caches.open(TEMPLATE_VERSIONS_CACHE);
      const { keys } = await cacheTemplateZip({ baseKey, cache, zip });

      if (cancelled) {
        await Promise.all(keys.map((key) => cache.delete(key)));
        return;
      }

      keysRef.current = keys;

      const config = CONFIG_JSON in zip.files ? await readConfig(zip) : {};

      setPreview({
        aspectRatio: usableRatio(config.aspectRatio),
        loading: false,
        payload: {
          contentOptions: (config.params ?? []).map((param, index) => ({
            array: param.array ?? false,
            fileId: null,
            id: index + 1,
            kind: param.kind.toUpperCase(),
            name: param.name,
            value: param.defaultValue ?? null,
          })),
          fileId: 0,
          id: 0,
          name: nameRef.current,
          // The `url` param is required by the route but unused: the service
          // worker matches the cache with `ignoreSearch` and never fetches.
          templateUri: `/template-versions/${versionId}/index.html?url=preview`,
        },
        unavailable: false,
      });
    };

    // Unreadable zip, or `config.json` that isn't parseable JSON. The server
    // reports the authoritative reason on Create; here we only need to stop
    // claiming no file has been chosen.
    build().catch(() => {
      if (!cancelled) setPreview(EMPTY);
    });

    return () => {
      cancelled = true;
    };
  }, [file]);

  useEffect(() => () => void evict(), []);

  return preview;
};
